import React, { useState, useEffect } from "react";
import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import {
  useToast,
  Box,
  Select,
  Input,
  Button,
  Skeleton,
  Heading,
  SimpleGrid,
  useColorModeValue,
  InputGroup,
  InputAddon,
  InputRightAddon,
} from "@chakra-ui/react";
import { t } from "@lingui/core/macro";
import { Line } from "react-chartjs-2";

type IDevice = { id: number; device_name: string };
type SeriesPoint = {
  timestamp: string;
  temperature: number | null;
  humidity: number | null;
  precipitation: number | null;
  wind_speed: number | null;
  max_wind_speed: number | null;
  wind_direction: number | null;
};

const intervals = [
  { key: "hourly", label: "Hourly" },
  { key: "daily", label: "Daily" },
];

const WeatherStationView: React.FC = () => {
  const api = useAxios();
  const toast = useToast();
  const bg = useColorModeValue("white", "gray.900");

  const [devices, setDevices] = useState<IDevice[]>([]);
  const [deviceId, setDeviceId] = useState<number>();
  const [lastDays, setLastDays] = useState<number>(30);
  const [interval, setInterval] = useState<string>(intervals[0].key);
  const [series, setSeries] = useState<SeriesPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const dRes = await api.get("frontend/user-devices/");
        setDevices(dRes.data);
        if (dRes.data[0]) setDeviceId(dRes.data[0].id);
      } catch {
        toast({
          title: "Error loading devices",
          status: "error",
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const run = async () => {
    if (!deviceId) return;
    setFetching(true);
    try {
      const resp = await api.get(`agtech/weather-station/data/${deviceId}/`, {
        params: { last_number_of_days: lastDays, interval },
      });
      setSeries(resp.data.series);
    } catch {
      toast({
        title: "Error fetching weather data",
        status: "error",
        isClosable: true,
      });
    } finally {
      setFetching(false);
    }
  };

  const chartData = {
    labels: series.map((p) => {
      const d = new Date(p.timestamp);
      if (interval === "daily") {
        return d.toLocaleDateString();
      }
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
      });
    }),
    datasets: [
      {
        label: "Temperature",
        data: series.map((p) => p.temperature),
        borderColor: "red",
        fill: false,
        yAxisID: "y1",
      },
      {
        label: "Humidity",
        data: series.map((p) => p.humidity),
        borderColor: "green",
        fill: false,
        yAxisID: "y1",
      },
      {
        label: "Precipitation",
        data: series.map((p) => p.precipitation),
        borderColor: "blue",
        backgroundColor: "blue",
        fill: true,
        yAxisID: "y2",
        type: "bar",
      },
      {
        label: "Wind Speed",
        data: series.map((p) => p.wind_speed),
        borderColor: "orange",
        fill: false,
        yAxisID: "y1",
      },
      {
        label: "Wind Gust",
        data: series.map((p) => p.max_wind_speed),
        borderColor: "purple",
        fill: false,
        yAxisID: "y1",
      },
    ],
  };

  const chartOptions = {
    scales: {
      yAxes: [
        { id: "y1", type: "linear", position: "left" },
        {
          type: "linear",
          id: "y2",
          position: "right",
          scaleLabel: {
            display: true,
            labelString: "mm", // Label for the second y-axis
          },
        },
      ],
      xAxes: [{}],
    },
    tooltips: {
      callbacks: {
        label: function (tooltipItem: any, data: any) {
          const label = data.datasets[tooltipItem.datasetIndex].label || "";
          const value = parseFloat(tooltipItem.value);
          return `${label}: ${value.toFixed(1)}`;
        },
      },
    },
  };

  // get latest data point
  const latest = series.length
    ? series[series.length - 1]
    : {
        temperature: null,
        humidity: null,
        precipitation: null,
        wind_speed: null,
        max_wind_speed: null,
        wind_direction: null,
      };

  return (
    <Navbar>
      <Box maxW="800px" mx="auto" p={4} bg={bg} borderRadius="md">
        <Heading mb={4}>Weather Station Data</Heading>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={4}>
          {loading ? (
            <Skeleton height="40px" />
          ) : (
            <Select value={deviceId} onChange={(e) => setDeviceId(+e.target.value)}>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.device_name}
                </option>
              ))}
            </Select>
          )}
          <InputGroup>
            <Input
              type="number"
              value={lastDays}
              onChange={(e) => setLastDays(+e.target.value)}
              placeholder={t`Last days`}
            />
            <InputRightAddon>days</InputRightAddon>
          </InputGroup>
          <Select value={interval} onChange={(e) => setInterval(e.target.value)}>
            {intervals.map((i) => (
              <option key={i.key} value={i.key}>
                {i.label}
              </option>
            ))}
          </Select>
          <Button onClick={run} colorScheme="teal" isLoading={fetching}>
            Load
          </Button>
        </SimpleGrid>
        {series.length > 0 && (
          <>
            <Box mb={4}>
              <Heading size="md" mb={2}>
                Current Conditions
              </Heading>
              <SimpleGrid columns={6} spacing={4}>
                <Box>
                  Temperature:{" "}
                  {latest.temperature != null ? latest.temperature.toFixed(1) + "°C" : "N/A"}
                </Box>
                <Box>
                  Humidity: {latest.humidity != null ? latest.humidity.toFixed(1) + "%" : "N/A"}
                </Box>
                <Box>
                  Precipitation:{" "}
                  {latest.precipitation != null ? latest.precipitation.toFixed(2) + " mm" : "N/A"}
                </Box>
                <Box>
                  Wind Speed:{" "}
                  {latest.wind_speed != null ? latest.wind_speed.toFixed(1) + " m/s" : "N/A"}
                </Box>
                <Box>
                  Wind Gust:{" "}
                  {latest.max_wind_speed != null
                    ? latest.max_wind_speed.toFixed(1) + " m/s"
                    : "N/A"}
                </Box>
                <Box>
                  Wind Direction:{" "}
                  {latest.wind_direction != null ? latest.wind_direction.toFixed(0) + "°" : "N/A"}
                </Box>
              </SimpleGrid>
            </Box>
            <Box height={400} mb={4}>
              <Line data={chartData} options={chartOptions} />
            </Box>
            <Box height={300} mb={4}>
              <Heading size="md" mb={2}>
                Wind Direction
              </Heading>
              <Line
                data={{
                  labels: chartData.labels,
                  datasets: [
                    {
                      label: "Wind Direction",
                      data: series.map((p) => p.wind_direction),
                      borderColor: "teal",
                      fill: false,
                      showLine: false,
                      pointRadius: 2,
                    },
                  ],
                }}
                options={{
                  scales: {
                    yAxes: [{ ticks: { min: 0, max: 360, stepSize: 90 } }],
                    xAxes: [{}],
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Navbar>
  );
};

export default WeatherStationView;
